import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import {
  Alert,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconEye,
  IconFileDescription,
  IconRefresh,
} from "@tabler/icons-react";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  useGrossingRequisitions as useGrossingDocuments,
  useGrossingRequisitionViewUrl as useGrossingDocumentViewUrl,
} from "../../hooks/useGrossingRequisitions";
import type { GrossingDocumentMetadata } from "../../services/grossingRequisitions.service";

interface GrossingRequisitionCardProps {
  caseId: string;
}

interface HttpError {
  response?: { status?: number };
}

const getErrorStatus = (error: unknown): number | undefined =>
  (error as HttpError | undefined)?.response?.status;

const getViewErrorMessage = (error: unknown): string => {
  const status = getErrorStatus(error);
  if (status === 401 || status === 403) return "This document is unavailable.";
  if (status === 404) return "This document is no longer available.";
  return "The document could not be opened. Try again.";
};

export default function GrossingRequisitionCard({
  caseId,
}: GrossingRequisitionCardProps) {
  const canView = useHasPermission("grossing.read", "documents.download");
  const documentsQuery = useGrossingDocuments(caseId, canView);
  const viewUrlMutation = useGrossingDocumentViewUrl();
  const requestSequence = useRef(0);
  const viewRequestInFlight = useRef(false);
  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState<GrossingDocumentMetadata | null>(
    null
  );
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [renderError, setRenderError] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);

  const documents = useMemo(
    () => documentsQuery.data ?? [],
    [documentsQuery.data]
  );
  const documentsById = useMemo(
    () => new Map(documents.map((item) => [item.id, item])),
    [documents]
  );
  const metadataStatus = useMemo(
    () => getErrorStatus(documentsQuery.error),
    [documentsQuery.error]
  );
  const isMetadataUnauthorized = useMemo(
    () => metadataStatus === 401 || metadataStatus === 403,
    [metadataStatus]
  );

  useEffect(
    () => () => {
      requestSequence.current += 1;
      viewRequestInFlight.current = false;
    },
    []
  );

  const requestView = useCallback(
    async (document: GrossingDocumentMetadata) => {
      if (viewRequestInFlight.current) return;
      viewRequestInFlight.current = true;
      const sequence = requestSequence.current + 1;
      requestSequence.current = sequence;
      setSelected(document);
      setOpened(true);
      setViewUrl(null);
      setViewError(null);
      setRenderError(false);
      const canPreview = document.kind !== "unsupported";
      setIsViewLoading(canPreview);
      if (!canPreview) {
        viewRequestInFlight.current = false;
        return;
      }
      try {
        const result = await viewUrlMutation.mutateAsync({
          caseId,
          fileId: document.id,
        });
        if (requestSequence.current !== sequence) return;
        setViewUrl(result.url);
      } catch (error) {
        if (requestSequence.current === sequence) {
          setViewError(getViewErrorMessage(error));
        }
      } finally {
        if (requestSequence.current === sequence) {
          viewRequestInFlight.current = false;
          setIsViewLoading(false);
        }
      }
    },
    [caseId, viewUrlMutation]
  );

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const documentId = event.currentTarget.dataset.documentId;
      const document = documentId ? documentsById.get(documentId) : undefined;
      if (document) void requestView(document);
    },
    [documentsById, requestView]
  );

  const handleClose = useCallback(() => {
    requestSequence.current += 1;
    viewRequestInFlight.current = false;
    setOpened(false);
    setSelected(null);
    setViewUrl(null);
    setViewError(null);
    setRenderError(false);
    setIsViewLoading(false);
  }, []);

  const handleMetadataRetry = useCallback(() => {
    void documentsQuery.refetch();
  }, [documentsQuery]);

  const handleViewRetry = useCallback(() => {
    if (selected) void requestView(selected);
  }, [requestView, selected]);

  const handleRenderError = useCallback(() => {
    setViewUrl(null);
    setRenderError(true);
  }, []);

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Group gap="xs">
          <IconFileDescription size={18} />
          <Text fw={600}>Documents</Text>
        </Group>

        {!canView && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            Documents unavailable.
          </Alert>
        )}

        {canView && documentsQuery.isLoading && (
          <Group gap="xs" aria-label="Loading documents">
            <Loader size="xs" />
            <Text size="sm">Checking for documents...</Text>
          </Group>
        )}

        {canView && documentsQuery.isError && (
          <Alert
            color={isMetadataUnauthorized ? "yellow" : "red"}
            icon={<IconAlertCircle size={16} />}
            title="Documents unavailable"
          >
            <Stack gap="xs">
              <Text size="sm">
                {isMetadataUnauthorized
                  ? "Document access is unavailable."
                  : "Documents could not be checked. Try again."}
              </Text>
              {!isMetadataUnauthorized && (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconRefresh size={14} />}
                  onClick={handleMetadataRetry}
                  loading={documentsQuery.isFetching}
                >
                  Retry
                </Button>
              )}
            </Stack>
          </Alert>
        )}

        {canView && documentsQuery.isSuccess && documents.length === 0 && (
          <Text size="sm" c="dimmed">
            No documents available.
          </Text>
        )}

        {canView && documentsQuery.isSuccess && documents.length > 1 && (
          <Text size="xs" c="dimmed">
            Select the document you want to view.
          </Text>
        )}

        {canView && documentsQuery.isSuccess && documents.length > 0 && (
          <Stack gap="xs">
            {documents.map((document) => (
              <Button
                key={document.id}
                data-document-id={document.id}
                variant="light"
                size="xs"
                leftSection={<IconEye size={14} />}
                onClick={handleOpen}
                disabled={isViewLoading}
                loading={isViewLoading && selected?.id === document.id}
              >
                View {document.label}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>

      <Modal
        opened={opened}
        onClose={handleClose}
        title={selected?.label ?? "Document"}
        size="90%"
        centered
        styles={{
          body: { padding: 0 },
          content: { display: "flex", flexDirection: "column" },
        }}
      >
        <Box style={{ height: "80vh", minHeight: 0 }}>
          {isViewLoading && (
            <Center style={{ height: "100%" }}>
              <Stack align="center" gap="xs">
                <Loader size="md" />
                <Text size="sm">Loading document...</Text>
              </Stack>
            </Center>
          )}

          {viewError && !isViewLoading && (
            <Box p="md">
              <Alert
                color="red"
                icon={<IconAlertCircle size={16} />}
                title="Could not open document"
              >
                <Stack gap="xs">
                  <Text size="sm">{viewError}</Text>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconRefresh size={14} />}
                    onClick={handleViewRetry}
                  >
                    Retry
                  </Button>
                </Stack>
              </Alert>
            </Box>
          )}

          {renderError && !viewError && (
            <Box p="md">
              <Alert
                color="red"
                icon={<IconAlertCircle size={16} />}
                title="Document cannot be displayed"
              >
                <Stack gap="xs">
                  <Text size="sm">
                    This document may be unsupported or unavailable. Try again.
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconRefresh size={14} />}
                    onClick={handleViewRetry}
                    loading={isViewLoading}
                  >
                    Retry
                  </Button>
                </Stack>
              </Alert>
            </Box>
          )}

          {viewUrl && selected?.kind === "pdf" && !renderError && (
            <iframe
              src={`${viewUrl}#zoom=FitW`}
              title="Document PDF"
              onError={handleRenderError}
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          )}

          {viewUrl && selected?.kind === "image" && !renderError && (
            <img
              src={viewUrl}
              alt={selected.label}
              onError={handleRenderError}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          )}

          {selected?.kind === "unsupported" && !isViewLoading && (
            <Box p="md">
              <Alert
                color="red"
                icon={<IconAlertCircle size={16} />}
                title="Document cannot be displayed"
              >
                This document format cannot be previewed here.
              </Alert>
            </Box>
          )}
        </Box>
      </Modal>
    </Paper>
  );
}
